<script setup lang="ts">
import { ref } from "vue";

import Icon from "@/components/Icon.vue";

interface Props {
    stop?: boolean;
    disabled?: boolean;
    iconHighlight?: boolean;
    icon?: string;
    iconClass?: string;
    type?: string;
    iconPosition?: string;
    // biome-ignore lint/complexity/noBannedTypes: Init Biome
    action?: Function;
}

const props = withDefaults(defineProps<Props>(), {
    iconPosition: "left",
});

const emit = defineEmits<(e: "click", event: MouseEvent) => void>();

const loading = ref(false);

async function handleClick(event: MouseEvent) {
    if (props.stop) {
        event.stopPropagation();
    }
    if (!props.disabled && !loading.value) {
        if (props.action) {
            loading.value = true;
            try {
                await props.action();
            } catch (e) {
                loading.value = false;
                throw e;
            }
            loading.value = false;
        } else {
            emit("click", event);
        }
    }
}
</script>

<template>
    <div @click="handleClick" :class="['Action', 'btn', { disabled: disabled || loading }]">
        <template v-if="!icon">
            <slot></slot>
        </template>
        <template v-else>
            <template v-if="$slots.default">
                <template v-if="iconHighlight">
                    <div class="icon" v-if="iconPosition === 'left'">
                        <Icon :class="iconClass" :icon="icon" />
                    </div>
                    <div><slot></slot></div>
                    <div class="icon" v-if="iconPosition === 'right'">
                        <Icon :class="iconClass" :icon="icon" />
                    </div>
                </template>
                <template v-else>
                    <Icon v-if="iconPosition === 'left'" :class="iconClass" :icon="icon" />
                    <div>
                        <slot></slot>
                    </div>
                    <Icon v-if="iconPosition === 'right'" :class="iconClass" :icon="icon" />
                </template>
            </template>
            <Icon v-else :class="iconClass" :icon="icon" />
        </template>
    </div>
</template>

<style scoped lang="scss">
.btn {
    border-radius: 10px;
    padding: calc($padding / 3) calc($padding / 2);
    font-size: 16px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    width: auto;
    font-weight: $medium;
    text-align: center;
    transition: 125ms linear;
    user-select: none;
    height: 45px;
    background: $white;
    color: $grey;

    border: none;
    &.validate,
    &.immediate-meeting {
        background-color: $lightgreen;
        color: white;
    }
    &.new-interview {
        background-color: $lightpurple2;
        color: white;
    }
    &.reject {
        background-color: $lightred;
        color: white;
    }
    &.validate-placed {
        background: $purplegradient;
        color: white;
    }

    .pulse {
        padding-top: 0;
    }

    & + & {
        margin-left: calc($padding / 2);
    }

    &.small-icon {
        .Icon {
            &,
            svg {
                width: 15px;
                height: 15px;
            }
        }
    }

    &.uppercase {
        text-transform: uppercase;
    }

    svg {
        fill: $grey;
    }

    &.round {
        border-radius: 30px;
    }

    &:hover {
        opacity: 0.85;
    }

    &.purpleneon {
        background: $white;
        color: $purple;
        svg {
            fill: $purple;
            .cls-2 {
                fill: $white;
            }
        }
        border: 1px solid $purple;
    }

    &.redneon {
        background: $white;
        color: $lightred;
        svg {
            fill: $lightred;
            .cls-2 {
                fill: $white;
            }
        }
        border: 1px solid $lightred;
    }

    &.purple {
        background: $purple;
        color: $white;
        svg {
            fill: $white;
            .cls-2 {
                fill: $purple;
            }
        }
        border-color: $purple;
    }

    &.teal {
        background: $teal;
        color: $white;
        svg {
            fill: $white;
            .cls-2 {
                fill: $teal;
            }
        }
        border-color: $teal;
    }

    &.red {
        background: $red;
        color: $white;
        svg {
            fill: $white;
            .cls-2 {
                fill: $red;
            }
        }
        border-color: $red;
    }

    &.grey {
        background: $grey;
        color: $white;
        svg {
            fill: $white;
            .cls-2 {
                fill: $grey;
            }
        }
        border-color: $grey;
    }
    &.lightgrey {
        background: $lightgrey2;
        color: $grey;
        svg {
            fill: $grey;
            .cls-2 {
                fill: $lightgrey2;
            }
        }
        border-color: $lightgrey2;
    }

    &.white {
        background: $white;
        color: $grey;
        border: none;
        svg {
            fill: $grey;
            .cls-2 {
                fill: $lightgrey2;
            }
        }
    }

    &.darkblue {
        background: $darkblue;
        color: $purple;
        svg {
            fill: $purple;
            .cls-2 {
                fill: $purple;
            }
        }
    }

    &.darkblue2 {
        background: $darkblue;
        color: $white;
        svg {
            fill: $white;
            .cls-2 {
                fill: $white;
            }
        }
    }

    &.darkgrey {
        background: $lightgrey2;
        color: $darkgrey;
        border: none;
        svg {
            fill: $darkgrey;
            .cls-2 {
                fill: $lightgrey2;
            }
        }
    }

    &.lightblack {
        background: $lightblack;
        color: $white;
        border: none;
        svg {
            fill: $white;
            .cls-2 {
                fill: $white;
            }
        }
    }

    &.lightred {
        background: $lightred;
        color: $white;
        border: none;
        svg {
            fill: $white;
            .cls-2 {
                fill: $white;
            }
        }
    }

    &.disabled {
        cursor: not-allowed;
        opacity: 0.25;
        &:hover {
            opacity: 0.25;
        }
    }

    &.giant {
        padding: 0;
        border-color: $lightgrey;
        .icon {
            background: $purple;
            border-radius: 10px;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            padding: calc($padding / 3);
            .Icon,
            svg {
                width: 35px;
                height: 35px;
                fill: $white;
            }
        }

        & > div {
            padding: 0 calc($padding / 1.5);
            font-size: 22px;
            color: white;
            font-weight: $regular;
        }

        &.decrease {
            & > div {
                font-size: 16px !important;
            }

            .Icon {
                &,
                svg {
                    width: 30px !important;
                    height: 30px !important;
                }
            }
            &.grey {
                background: $white;
            }

            &.darktext {
                color: $darkgrey;
            }
        }
    }

    &.bigRound {
        border-radius: 1200px;
    }

    &.textPurple {
        color: $purple;
    }
}
</style>
